import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Mail, MapPin, MessageCircle, Phone, Star, User, X } from 'lucide-react';
import { getVisitDependantName } from '../../lib/visitUtils';
import { getProfilePhotoUrl } from '../../lib/profileUtils';
import { getApiErrorMessage } from '../../lib/apiError';

const officerName = (officer, visit) => (
  officer?.name ||
  `${officer?.firstName || officer?.first_name || ''} ${officer?.lastName || officer?.last_name || ''}`.trim() ||
  visit?.careOfficerName ||
  visit?.careOfficerSnapshot?.name ||
  'Care officer'
);

const StarRating = ({ value, onChange, disabled = false }) => (
  <div className="flex items-center gap-1">
    {[1, 2, 3, 4, 5].map((score) => (
      <button
        key={score}
        type="button"
        disabled={disabled}
        onClick={() => onChange(score)}
        className={`rounded-full p-1 ${disabled ? 'cursor-default' : 'hover:bg-[#F5E8EA]'}`}
        aria-label={`Rate ${score} star${score === 1 ? '' : 's'}`}
      >
        <Star className={`h-6 w-6 ${score <= value ? 'fill-[#C9A961] text-[#C9A961]' : 'text-[#D8D5D0]'}`} />
      </button>
    ))}
  </div>
);

const CareOfficerProfileModal = ({
  officer,
  visit,
  existingRating,
  canRate = false,
  onClose,
  onSubmitRating,
  onStartChat,
}) => {
  const currentRating = existingRating?.subscriberRating || existingRating?.subscriber_rating || existingRating?.dependantRating || existingRating?.dependant_rating || null;
  const [score, setScore] = useState(currentRating?.score || 0);
  const [feedback, setFeedback] = useState(currentRating?.feedback || '');
  const [saving, setSaving] = useState(false);
  const [startingChat, setStartingChat] = useState(false);

  const name = officerName(officer, visit);
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'CO';
  const profilePhotoUrl = getProfilePhotoUrl(officer) || getProfilePhotoUrl(visit?.careOfficerSnapshot || visit?.care_officer_snapshot);
  const phone = officer?.phone || officer?.primaryPhone || officer?.primary_phone || visit?.careOfficerSnapshot?.phone;
  const email = officer?.email || officer?.workEmail || officer?.work_email;
  const gender = officer?.gender;
  const languages = officer?.languages || officer?.preferredLanguages || officer?.preferred_languages || officer?.caregiverLanguages || [];
  const territory = officer?.preferredTerritory || officer?.preferred_territory || officer?.assignedRegion || officer?.assigned_region || officer?.coverageZone?.state || officer?.coverage_zone?.state;
  const rating = officer?.rating || officer?.averageRating || 0;
  const ratingCount = officer?.ratingCount || officer?.rating_count || 0;
  const completedVisits = officer?.totalVisits || officer?.total_visits || officer?.visitsCompleted || officer?.visits_completed || 0;
  const normalizedLanguages = Array.isArray(languages) ? languages.join(', ') : languages;
  const officerId = officer?.id || officer?._id || visit?.careOfficerId || visit?.care_officer_id;

  const visitCanBeRated = canRate && visit && !['Scheduled', 'Cancelled'].includes(visit.status);
  const visitLabel = useMemo(() => (
    visit ? `${getVisitDependantName(visit)} visit` : ''
  ), [visit]);

  const handleSubmit = async () => {
    if (!score) {
      toast.error('Choose a star rating first.');
      return;
    }
    if (!onSubmitRating || !visit) return;
    setSaving(true);
    try {
      await onSubmitRating(visit.id, score, feedback);
      toast.success('Visit rating saved.');
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to save rating.'));
    } finally {
      setSaving(false);
    }
  };

  const handleStartChat = async () => {
    if (!onStartChat || !officerId) return;
    setStartingChat(true);
    try {
      await onStartChat({ officer, visit, officerId });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to open chat.'));
    } finally {
      setStartingChat(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-[#1A1A1A]/50 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] shadow-none">
        <div className="flex items-start justify-between gap-4 border-b border-[#E8E6E3] p-5">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-full bg-[#9F1D35] font-semibold text-[#FAFAF7]">
              {profilePhotoUrl ? (
                <img src={profilePhotoUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                initials
              )}
            </div>
            <div>
              <h2 className="font-serif text-2xl font-semibold text-[#1A1A1A]">{name}</h2>
              <p className="mt-1 text-sm text-[#5A5A5A]">Magnolia care officer</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 text-[#5A5A5A] hover:bg-[#F5E8EA]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 p-5">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border border-[#E8E6E3] p-4">
              <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">Rating</p>
              <p className="mt-2 font-serif text-2xl font-semibold text-[#9F1D35]">{rating || '-'}</p>
              <p className="text-xs text-[#5A5A5A]">{ratingCount} rating{ratingCount === 1 ? '' : 's'}</p>
            </div>
            <div className="rounded-2xl border border-[#E8E6E3] p-4">
              <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">Visits</p>
              <p className="mt-2 font-serif text-2xl font-semibold text-[#9F1D35]">{completedVisits || '-'}</p>
              <p className="text-xs text-[#5A5A5A]">Completed</p>
            </div>
            <div className="rounded-2xl border border-[#E8E6E3] p-4">
              <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">Gender</p>
              <p className="mt-2 text-sm font-medium text-[#1A1A1A]">{gender || 'Not listed'}</p>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-[#E8E6E3] p-4">
              <p className="mb-2 flex items-center gap-2 text-sm font-medium text-[#1A1A1A]"><MapPin className="h-4 w-4 text-[#9F1D35]" />Coverage</p>
              <p className="text-sm text-[#5A5A5A]">{territory || 'Coverage not listed'}</p>
              {normalizedLanguages && <p className="mt-2 text-sm text-[#5A5A5A]">Languages: {normalizedLanguages}</p>}
            </div>
            <div className="rounded-2xl border border-[#E8E6E3] p-4">
              <p className="mb-2 flex items-center gap-2 text-sm font-medium text-[#1A1A1A]"><User className="h-4 w-4 text-[#9F1D35]" />Contact</p>
              <div className="flex flex-wrap gap-2">
                {onStartChat && officerId && (
                  <button
                    type="button"
                    onClick={handleStartChat}
                    disabled={startingChat}
                    className="inline-flex items-center gap-2 rounded-full bg-[#9F1D35] px-3 py-2 text-sm font-medium text-[#FAFAF7] disabled:opacity-60"
                  >
                    <MessageCircle className="h-4 w-4" />
                    {startingChat ? 'Opening...' : 'Chat'}
                  </button>
                )}
                {phone && <a href={`tel:${phone}`} className="inline-flex items-center gap-2 rounded-full border border-[#9F1D35] px-3 py-2 text-sm font-medium text-[#9F1D35]"><Phone className="h-4 w-4" />Call</a>}
                {email && <a href={`mailto:${email}`} className="inline-flex items-center gap-2 rounded-full border border-[#9F1D35] px-3 py-2 text-sm font-medium text-[#9F1D35]"><Mail className="h-4 w-4" />Email</a>}
                {!phone && !email && !onStartChat && <p className="text-sm text-[#5A5A5A]">Contact details are not listed.</p>}
              </div>
            </div>
          </div>

          {visitCanBeRated && (
            <div className="rounded-2xl border border-[#E8E6E3] bg-white p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h3 className="font-serif text-xl font-semibold text-[#1A1A1A]">Rate this visit</h3>
                  <p className="text-sm text-[#5A5A5A]">{visitLabel}</p>
                </div>
                <StarRating value={score} onChange={setScore} disabled={saving} />
              </div>
              <textarea
                value={feedback}
                onChange={(event) => setFeedback(event.target.value)}
                rows={4}
                className="mt-4 w-full rounded-2xl border border-[#D8D5D0] bg-[#FAFAF7] p-3 text-sm outline-none focus:border-[#9F1D35]"
                placeholder="Optional feedback for the care team..."
              />
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className="mt-3 inline-flex w-full items-center justify-center rounded-full bg-[#9F1D35] px-5 py-3 text-sm font-medium text-[#FAFAF7] disabled:opacity-60"
              >
                {saving ? 'Saving rating...' : existingRating ? 'Update rating' : 'Submit rating'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CareOfficerProfileModal;
